import type { VnRegion } from '@tcr-erp/shared';
import api from './api';

/**
 * EG-341 — Warehouse management theo store/chi nhánh.
 */

export interface Warehouse {
  finished_good_warehouse_id?: string;
  _id?: string;
  tenant_id?: string;
  store_id?: string | null;
  /** Tên chi nhánh gắn với kho — BE trả kèm qua $lookup ở list(); dùng để phân biệt kho trùng tên. */
  store_name?: string;
  code: string;
  name: string;
  name_alias?: string;
  is_main_warehouse?: boolean;
  group_name?: string;
  /** TCR-675 — vùng miền của kho, đối chiếu với miền giao của đơn ECom. */
  region?: VnRegion | null;
  state?: number; // 1 = ENABLED, 0 = DISABLED
  targets?: string[];
  account_id?: string | null;
  account_code?: string;
  account_name?: string;
  allow_negative?: boolean;
  allow_negative_stock?: boolean;
  is_stock_taking?: boolean;
  lot_tracking?: boolean;
  location_tracking?: boolean;
  stock_alert_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface WarehouseListResponse {
  data: Warehouse[];
  total: number;
  page: number;
  page_size: number;
}

export interface CreateWarehousePayload {
  code: string;
  name: string;
  name_alias?: string;
  store_id: string;
  is_main_warehouse?: boolean;
  group_name?: string | null;
  region?: VnRegion | null;
  state?: number;
  targets?: string[];
  account_id?: string | null;
  allow_negative?: boolean;
  lot_tracking?: boolean;
  location_tracking?: boolean;
  stock_alert_enabled?: boolean;
}

export type UpdateWarehousePayload = Partial<CreateWarehousePayload>;

export interface WarehouseListQuery {
  store_id?: string; // 'null' để lọc kho không gắn store
  state?: '0' | '1' | '';
  search?: string;
  page?: number;
  page_size?: number;
}

/** TCR-1721 — một dòng trong ô chọn "Tài khoản kho" của form tạo/sửa kho. */
export interface WarehouseAccountOption {
  _id: string;
  account_code: string;
  name: string;
}

/**
 * TCR-1973 — tải file .xlsx về máy. Cùng pattern với `codStatementService`
 * (`src/services/cod-statement.service.ts`): request thẳng, không qua async job.
 */
async function downloadBlob(url: string, params: Record<string, string | undefined>, fileName: string): Promise<void> {
  const response = await api.get<Blob>(url, { params, responseType: 'blob' });
  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(objectUrl);
}

export const warehouseService = {
  list: (params: WarehouseListQuery = {}) =>
    api.get<WarehouseListResponse>('/inventory/warehouses', { params }).then((r) => r.data),

  /**
   * TCR-1973 — nút "Xuất Excel" ở màn "Quản lý kho hàng".
   *
   * Trước fix, FE gọi `ExcelActionDropdown` → `POST /excel/export-jobs` (async job), gate bằng
   * `excel_export.manage` — permission CHỈ Owner có theo thiết kế, nên Thủ kho (có
   * `inventory.warehouse.view` qua wildcard `inventory.*`, permission mà role-seed.service.ts ghi
   * RÕ "bao gồm cả export") không bao giờ thấy mục "Xuất Excel". Route `GET
   * /inventory/warehouses/export` (gác đúng `inventory.warehouse.view`) nay trả file .xlsx TRỰC
   * TIẾP trong response (đồng bộ, không qua job) — gọi thẳng route này thay vì
   * `ExcelActionDropdown`/`excelService.createExportJob()`.
   */
  exportList: (filters: WarehouseListQuery = {}) =>
    downloadBlob(
      '/inventory/warehouses/export',
      { store_id: filters.store_id, state: filters.state, search: filters.search },
      'danh-sach-kho.xlsx',
    ),

  get: (id: string) =>
    api.get<Warehouse>(`/inventory/warehouses/${id}`).then((r) => r.data),

  create: (body: CreateWarehousePayload) =>
    api.post<Warehouse>('/inventory/warehouses', body).then((r) => r.data),

  update: (id: string, body: UpdateWarehousePayload) =>
    api.patch<Warehouse>(`/inventory/warehouses/${id}`, body).then((r) => r.data),

  remove: (id: string) =>
    api.delete<{ ok: true; mode: string }>(`/inventory/warehouses/${id}`).then((r) => r.data),

  /**
   * TCR-1721 — tài khoản kho (151–157) cho ô "Tài khoản kho" của form tạo/sửa kho.
   *
   * Trước đây form lấy dữ liệu từ `GET /accounting/accounts`, route gác bằng quyền kế toán nên
   * thủ kho nhận 403 và ô select rỗng — mà field bắt buộc, nên không lưu nổi kho. Route này gác
   * bằng `inventory.warehouse.view` và BE đã lọc/sắp sẵn đúng nhánh tài khoản kho, nên FE không
   * cần lọc regex hay gọi nhiều trang như trước.
   */
  listAccountOptions: () =>
    api
      .get<{ data: WarehouseAccountOption[] }>('/inventory/warehouses/account-options')
      .then((r) => r.data),

  // ─── Warehouse Staff ──────────────────────────────────────

  listStaff: (warehouseId: string) =>
    api.get<{ data: WarehouseStaff[] }>(`/inventory/warehouses/${warehouseId}/staff`).then((r) => r.data),

  addStaff: (warehouseId: string, staffId: string, isPrimary = false) =>
    api.post<{ data: WarehouseStaff }>(`/inventory/warehouses/${warehouseId}/staff`, { staff_id: staffId, is_primary: isPrimary }).then((r) => r.data),

  setPrimaryStaff: (warehouseId: string, staffId: string) =>
    api.patch(`/inventory/warehouses/${warehouseId}/staff/${staffId}/primary`).then((r) => r.data),

  removeStaff: (warehouseId: string, staffId: string) =>
    api.delete(`/inventory/warehouses/${warehouseId}/staff/${staffId}`).then((r) => r.data),

  // ─── Kho phụ trách theo NHÂN VIÊN (chiều ngược của listStaff/addStaff/removeStaff) ───────
  // Phục vụ field "Kho phụ trách" ở drawer Sửa nhân viên: đọc 1 lần toàn bộ kho của NV và ghi
  // lại cả tập trong 1 request (BE diff thêm/gỡ, giữ nguyên cờ thủ kho chính của dòng không đổi).

  listStaffWarehouses: (staffId: string) =>
    api
      .get<{ data: { warehouse_ids: string[] } }>(`/inventory/staff-warehouses/${staffId}`)
      .then((r) => r.data.data.warehouse_ids),

  syncStaffWarehouses: (staffId: string, warehouseIds: string[]) =>
    api
      .put<{ data: { warehouse_ids: string[] } }>(`/inventory/staff-warehouses/${staffId}`, {
        warehouse_ids: warehouseIds,
      })
      .then((r) => r.data.data.warehouse_ids),
};

export interface WarehouseStaff {
  _id: string;
  warehouse_id: string;
  staff_id: { _id: string; name: string; staff_code: string; phone?: string } | string;
  is_primary: boolean;
  created_at: string;
}
